import React, { useState, useEffect, useCallback } from "react";
import { auditLogAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  History,
  ChevronLeft,
  ChevronRight,
  Plus,
  Edit,
  Trash2,
  FileText,
  User,
  FolderOpen,
} from "lucide-react";

export function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;
  
  // Filters
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (entityType) params.entity_type = entityType;
      if (action) params.action = action;
      
      const response = await auditLogAPI.getAll(params);
      setLogs(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      showMessage("Errore nel caricamento dei log", "error");
    } finally {
      setLoading(false);
    }
  }, [page, entityType, action]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case "create": return Plus;
      case "update": return Edit;
      case "delete": return Trash2;
      default: return History;
    }
  };

  const getActionColor = (actionType) => {
    switch (actionType) {
      case "create": return "bg-emerald-100 text-emerald-800 border-emerald-200";
      case "update": return "bg-amber-100 text-amber-800 border-amber-200";
      case "delete": return "bg-red-100 text-red-800 border-red-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getActionLabel = (actionType) => {
    switch (actionType) {
      case "create": return "Creazione";
      case "update": return "Modifica";
      case "delete": return "Eliminazione";
      default: return actionType;
    }
  };

  const getEntityIcon = (type) => {
    switch (type) {
      case "protocol": return FileText;
      case "user": return User;
      case "document_type": return FolderOpen;
      default: return History;
    }
  };

  const getEntityLabel = (type) => {
    switch (type) {
      case "protocol": return "Protocollo";
      case "user": return "Utente";
      case "document_type": return "Tipologia";
      default: return type;
    }
  };

  const formatChanges = (changes) => {
    if (!changes) return null;
    return Object.entries(changes).map(([key, value]) => {
      if (key === "attachments") return null;
      const label = {
        subject: "Oggetto",
        sender: "Mittente",
        recipient: "Destinatario",
        notes: "Note",
        document_type: "Tipologia",
        assigned_to: "Assegnato a",
        assigned_to_name: "Nome assegnato",
      }[key] || key;
      
      if (typeof value === "object" && value.old !== undefined) {
        return (
          <div key={key} className="text-xs">
            <span className="font-medium">{label}:</span>{" "}
            <span className="text-red-600 line-through">{value.old || "(vuoto)"}</span>
            {" → "}
            <span className="text-emerald-600">{value.new || "(vuoto)"}</span>
          </div>
        );
      }
      return (
        <div key={key} className="text-xs">
          <span className="font-medium">{label}:</span> {String(value)}
        </div>
      );
    }).filter(Boolean);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Message Banner */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          message.type === "error" 
            ? "bg-red-500 text-white" 
            : "bg-emerald-500 text-white"
        }`}>
          {message.text}
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
          <History size={32} className="text-[#2D7BB7]" />
          Audit Log
        </h1>
        <p className="text-muted-foreground mt-1">
          {total.toLocaleString("it-IT")} operazioni registrate
        </p>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <Select
              value={entityType || undefined}
              onValueChange={(val) => {
                setEntityType(val === "__all__" ? "" : val);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[200px] bg-background border-input" data-testid="filter-entity-type">
                <SelectValue placeholder="Tutte le entita" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutte le entita</SelectItem>
                <SelectItem value="protocol">Protocolli</SelectItem>
                <SelectItem value="user">Utenti</SelectItem>
                <SelectItem value="document_type">Tipologie</SelectItem>
              </SelectContent>
            </Select>
            
            <Select
              value={action || undefined}
              onValueChange={(val) => {
                setAction(val === "__all__" ? "" : val);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[200px] bg-background border-input" data-testid="filter-action">
                <SelectValue placeholder="Tutte le azioni" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutte le azioni</SelectItem>
                <SelectItem value="create">Creazione</SelectItem>
                <SelectItem value="update">Modifica</SelectItem>
                <SelectItem value="delete">Eliminazione</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-card border-border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Data/Ora</TableHead>
                <TableHead className="text-muted-foreground">Azione</TableHead>
                <TableHead className="text-muted-foreground">Tipo</TableHead>
                <TableHead className="text-muted-foreground">Elemento</TableHead>
                <TableHead className="text-muted-foreground">Utente</TableHead>
                <TableHead className="text-muted-foreground">Dettagli</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i} className="border-border">
                    {[...Array(6)].map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-6 bg-muted" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <History size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">Nessun log trovato</p>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => {
                  const ActionIcon = getActionIcon(log.action);
                  const EntityIcon = getEntityIcon(log.entity_type);
                  return (
                    <TableRow
                      key={log.id}
                      className="border-border hover:bg-muted/50"
                      data-testid={`audit-log-${log.id}`}
                    >
                      <TableCell className="text-foreground text-sm">
                        {formatDate(log.created_at)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getActionColor(log.action)} border flex items-center gap-1 w-fit`}>
                          <ActionIcon size={12} />
                          {getActionLabel(log.action)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-foreground">
                          <EntityIcon size={16} className="text-muted-foreground" />
                          {getEntityLabel(log.entity_type)}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-[#2D7BB7] text-sm">
                        {log.entity_name}
                      </TableCell>
                      <TableCell className="text-foreground text-sm">
                        {log.user_name}
                      </TableCell>
                      <TableCell className="max-w-[300px]">
                        {log.changes && (
                          <div className="space-y-1">
                            {formatChanges(log.changes)}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Pagina {page} di {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="border-border text-foreground disabled:opacity-50"
                data-testid="prev-page"
              >
                <ChevronLeft size={16} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="border-border text-foreground disabled:opacity-50"
                data-testid="next-page"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
